// PRD 06 §2.1 — header right-side actions:
//   - Status badge — two states per ADM.001: 변경됨(Changed) whenever the selected
//     row has undeployed changes; 배포됨(Deployed) once deployed and clean. No chip
//     on a fresh, untouched open.
//   - Last-deploy text whenever deploy history exists
//   - [PRO 전체 배포] button — enabled on a 공통, disease-group or disease-code row that
//     has changes and no empty module; never on 전체 (ADM.011, ADM.045 as of PRD 06 v1.21).
//
// FEATURE_025 — the status is decided by the SERVER for the selected row (it compares
// each item, question and guideline with its deployed copy), so it is the same in a
// new tab and for another operator. Nothing is counted from this browser.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Rocket } from 'lucide-react';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { toast } from '@/components/feedback/useToast';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

import { useResetCountQuery } from '../hooks/use-deployment-current-config';
import { useDeployMutation } from '../hooks/use-deployment-mutations';
import type {
  DeployState,
  DiseaseFilterOption,
  Module,
  ProItemRef,
} from '../types/pro-deployment.types';
import { deployLayerOf, type DeployLayer } from '../utils/disease-scope';
import { DirtyBadge } from '@/components/feedback/DirtyBadge';

interface DeployHeaderActionsProps {
  deploy: DeployState;
  modules: Module[];
  // The item rows, so empty modules and the confirm summary are counted on the selected row's items.
  items?: ProItemRef[];
  // ADM.012 — the currently-selected disease row, sent with the deploy so the audit row records it.
  diseaseFilter: string;
  // FEATURE_025 — the selected dropdown option; it names the layer a deploy of this row publishes.
  diseaseOption?: DiseaseFilterOption;
  // FEATURE_025 — whether an item belongs to the selected row. Omitted → every item.
  isVisible?: (item: ProItemRef) => boolean;
}

export function DeployHeaderActions({
  deploy,
  modules,
  items = [],
  diseaseFilter,
  diseaseOption,
  isVisible,
}: DeployHeaderActionsProps) {
  const { t } = useTranslation('pro-deployment');
  // SHCT-582 — PRD 21 §7.4 P-2: [PRO 전체 배포] stays on screen without PRO_MGMT:WRITE and is
  // `disabled` + 툴팁.
  const { canWrite, denyProps, tooltip } = useWriteGuard('PRO_MGMT');
  const deployMutation = useDeployMutation();

  const [confirmOpen, setConfirmOpen] = useState(false);
  // Set when a deploy attempt fails — flips the confirm modal into a retry state
  // (error copy + 재시도 label). Cleared on open / cancel / success.
  const [deployError, setDeployError] = useState(false);

  const inView = (item: ProItemRef) => (isVisible ? isVisible(item) : true);

  // ONE definition of "are there undeployed changes?", shared by the chip, the deploy button and the
  // summary: the server's status for the selected row.
  const hasChanges = deploy.status === 'CHANGED';
  // SHCT-595 — a scope with NO modules has nothing to publish, so it shows no status chip at all.
  const noModules = modules.length === 0;
  const showChanged = hasChanges && !noModules;
  const showDeployed = deploy.status === 'DEPLOYED' && !noModules;
  // §3.4 — keep "마지막 배포:" under BOTH Changed-after-deploy and Deployed.
  const showLastDeployed = !!deploy.lastDeployedAt;
  // ADM.011 / FEATURE_025 — only a module with no item at all blocks the deploy. A shared module that holds items
  // of other disease codes only does not block this code (decided 2026-09-13): the deploy simply takes nothing
  // from it. The server applies the same rule.
  const emptyModuleCount = modules.filter((m) => m.itemIds.length === 0).length;
  const blockedByEmptyModule = noModules || emptyModuleCount > 0;
  // ADM.045 / FEATURE_025 — the "전체" of [PRO 전체 배포] is every item of the selected row's LAYER: 공통, one
  // disease group, or one disease code. The 전체 row owns no layer and is never deployable, even when its chip
  // shows 변경됨; the server refuses it as well.
  const layer = deployLayerOf(diseaseFilter, diseaseOption);
  const isDeployableRow = layer !== null;
  const canDeployNow = isDeployableRow && hasChanges && !blockedByEmptyModule;

  // PRD 06 v1.21 §3.4 — the confirmation states how many in-progress questionnaires this deploy resets. Read
  // when the confirmation opens, so the number is current.
  const resetCountQuery = useResetCountQuery(diseaseFilter, confirmOpen && isDeployableRow);
  // A failed read is a system error: said once per failure, as a toast, and the deploy stays available.
  const resetCountFailed = confirmOpen && isDeployableRow && resetCountQuery.isError;
  const resetErrorReported = useRef(false);
  useEffect(() => {
    if (!resetCountFailed) {
      resetErrorReported.current = false;
      return;
    }
    if (!resetErrorReported.current) {
      resetErrorReported.current = true;
      toast.error(t('header.toasts.resetCountError'));
    }
  }, [resetCountFailed, t]);
  const resetCountLoading = confirmOpen && isDeployableRow && resetCountQuery.isLoading;

  // SHCT-728 요구사항 3 — say what this deploy will publish BEFORE it runs, from the server's markers
  // for the selected row: the modules with a change and the row's changed items.
  const serverDirtyModules = modules.filter((m) => m.dirty).length;
  const serverDirtyItems = items.filter((i) => i.dirty && inView(i)).length;
  const targetSummary = (() => {
    const rows: string[] = [];
    if (serverDirtyModules > 0)
      rows.push(t('header.confirm.targets.modules', { count: serverDirtyModules }));
    if (serverDirtyItems > 0)
      rows.push(t('header.confirm.targets.dirtyItems', { count: serverDirtyItems }));
    if (rows.length === 0) return t('header.confirm.targets.unknown');
    return `${t('header.confirm.targets.heading')}\n${rows.map((r) => `· ${r}`).join('\n')}`;
  })();

  const layerSentence = (l: DeployLayer | null): string => {
    if (l?.kind === 'COMMON') return t('header.confirm.layer.common');
    if (l?.kind === 'GROUP') return t('header.confirm.layer.group', { group: l.groupName });
    if (l?.kind === 'CODE') return t('header.confirm.layer.code', { code: l.code });
    return '';
  };
  const resetCount = resetCountQuery.data;
  const confirmMessage = [
    layerSentence(layer),
    typeof resetCount === 'number' ? t('header.confirm.resetCount', { count: resetCount }) : null,
    targetSummary,
  ]
    .filter((line): line is string => !!line)
    .join('\n\n');

  const handleConfirm = () => {
    deployMutation.mutate(
      {
        // The server counts the change summary itself and ignores this one (FEATURE_028); it is sent only
        // because the request shape still carries it.
        summary: {
          itemsCount: serverDirtyItems,
          surveysCount: 0,
          guidelinesCount: 0,
          moduleConfigChanged: serverDirtyModules > 0,
        },
        // ADM.012 — record the published disease code in the deploy audit.
        diseaseFilter,
      },
      {
        onSuccess: () => {
          toast.success(t('header.toasts.success'));
          setDeployError(false);
          setConfirmOpen(false);
        },
        onError: () => {
          toast.error(t('header.toasts.error'));
          setDeployError(true);
        },
      },
    );
  };

  return (
    <>
      <div
        className="content__actions"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
        {showDeployed ? (
          <span className="badge badge-success">{t('header.status.deployed')}</span>
        ) : null}
        {/* SHCT-604 — same wording source as the item-level badges. */}
        {showChanged ? <DirtyBadge /> : null}
        {showLastDeployed ? (
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--muted-foreground)' }}>
            {t('header.lastDeployed', { at: deploy.lastDeployedAt })}
          </span>
        ) : null}
        {/* Keyed off the real ADM.011 situation, NOT `blockedByEmptyModule` — that
            also covers the zero-module scope, where "{count}개" would read 0. Shown only
            on a row a deploy can run from. */}
        {canWrite && isDeployableRow && emptyModuleCount > 0 ? (
          <span
            role="alert"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--badge-warning-text)' }}
          >
            {t('header.emptyModuleBlock', { count: emptyModuleCount })}
          </span>
        ) : null}
        <button
          type="button"
          className="btn btn-default btn-primary"
          disabled={!canWrite || !canDeployNow || deployMutation.isPending}
          onClick={() => {
            setDeployError(false);
            setConfirmOpen(true);
          }}
          {...denyProps}
          // Permission outranks the ADM.011 empty-module hint: a Role that cannot deploy at all
          // has no use for the reason the deploy is currently blocked.
          title={
            tooltip ??
            (isDeployableRow && emptyModuleCount > 0
              ? t('header.emptyModuleBlock', { count: emptyModuleCount })
              : undefined)
          }
        >
          <Rocket size={16} />
          {t('header.deployButton')}
        </button>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={t('header.confirm.title')}
        message={deployError ? t('header.confirm.retryMessage') : confirmMessage}
        confirmLabel={
          deployError ? t('header.confirm.retryLabel') : t('header.confirm.confirmLabel')
        }
        // While the reset count is still being read, the confirm button waits for it: the number is what
        // tells the administrator whether this deploy discards patients' unfinished answers.
        loadingLabel={resetCountLoading ? t('state.loading') : t('header.confirm.loadingLabel')}
        loading={deployMutation.isPending || resetCountLoading}
        onConfirm={handleConfirm}
        onCancel={() => {
          if (deployMutation.isPending) return;
          setDeployError(false);
          setConfirmOpen(false);
        }}
      />
    </>
  );
}
